import { BasicButton, Button, PrimaryButton, TextButton } from '../../../../widgets/buttons';
import { PageFooterButtons } from '../../../../widgets/headers';
import { PageHeader } from '../../../../widgets/pageHeader';
import { VerticalLayout } from '../../../../widgets/layouts';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import _ from 'lodash';
import React, { FunctionComponent, useMemo } from 'react';
import { Section } from 'src/components/widgets/section';
import { Center } from 'src/shared/globals';
import { T, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { useProductConfigurationState } from '../../state';
import { ModelColor, isDefaultColor as isDefaultModelColor } from '../../state/model/modelColor';
import { ModelSide } from '../../state/model/modelSide';
import VariantRow from './variantRow';

const MappingLinkButton = styled(TextButton)`
    margin-right: 25px;
`;

const PageHeaderContainer = styled.div`
    margin: 20px 0px !important;

    > div {
        padding-bottom: 0px !important;
    }
`;

interface VariantsListProps {
    isSidesPerVariantEnabled: boolean;
    onSideAreaChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onSideImageChangeClick: (modelSide: ModelSide, modelColor: ModelColor) => void;
    onSideToggleClick: (colorGuid: string, sideGuid: string) => void;
    onVariantRemoveClick: (modelColor: ModelColor) => void;
    onVariantMoveUpClick: (modelColor: ModelColor) => void;
    onVariantMoveDownClick: (modelColor: ModelColor) => void;
    onVariantEditClick: (modelColor: ModelColor) => void;
    onVariantAddClick: (showConnectModelDialog?: boolean) => void;
    onPreviewClick: () => void;
    onSetMandatorySide: (value: boolean, modelSide: ModelSide) => void;
    onSetRotateSide: (value: boolean, model: ModelSide) => void;
    onClickJsonSettings: (modelColor: ModelColor) => void;
    unsupportedSalesChannelsVariantsIDs: string[];
    hasMappingMismatch: boolean;
    mismatchedVariantGuids: Set<string>;
    fixedVariantGuids: Set<string>;
    storeVariantsRemoved: boolean;
}

const VariantsList: FunctionComponent<VariantsListProps> = ({
    isSidesPerVariantEnabled,
    onSideImageChangeClick,
    onSideAreaChangeClick,
    onSideToggleClick,
    onVariantEditClick,
    onVariantRemoveClick,
    onVariantMoveDownClick,
    onVariantMoveUpClick,
    onVariantAddClick,
    onPreviewClick,
    onSetMandatorySide,
    onSetRotateSide,
    onClickJsonSettings,
    unsupportedSalesChannelsVariantsIDs,
    hasMappingMismatch,
    mismatchedVariantGuids,
    fixedVariantGuids,
    storeVariantsRemoved
}) => {
    const { state } = useProductConfigurationState();
    const colors = useMemo(
        () =>
            _.sortBy(
                state.product.colors.filter((x) => !isDefaultModelColor(x)),
                (color) => color.position
            ),
        [state.product.colors]
    );

    const additionalCodesFiltered = useMemo(() => {
        return state.product.additionalCodes.filter((ac) => {
            return !unsupportedSalesChannelsVariantsIDs.some((x) => toLocalID(x) === toLocalID(ac.salesChannelId));
        });
    }, [state.product.additionalCodes, unsupportedSalesChannelsVariantsIDs]);

    return (
        <React.Fragment>
            <VerticalLayout>
                <PageHeaderContainer>
                    <PageHeader
                        isBackVisible={false}
                        title={state.product.name}
                        actions={
                            <>
                                {colors.length > 0 && additionalCodesFiltered.length > 0 && (
                                    <MappingLinkButton onClick={() => onVariantAddClick(true)}>{T._('Mapping Sales Channel', 'Admin')}</MappingLinkButton>
                                )}
                                {colors.length > 0 && (
                                    <BasicButton disabled={storeVariantsRemoved} onClick={() => onVariantAddClick()}>
                                        {state.ecommerceSalesChannel.some((e) => e === 'NONE') ? T._('Edit attributes', 'Admin') : T._('Add', 'Admin')}
                                    </BasicButton>
                                )}
                            </>
                        }
                    />
                </PageHeaderContainer>

                {hasMappingMismatch && (
                    <NotificationBanner
                        type='WARNING'
                        title={T._('Variant mapping mismatch detected', 'Admin')}
                        content={T._(
                            'The variant options in your store appear to have changed since the last configuration. The current mapping may be incorrect and could cause out-of-stock errors. Please fix the variants highlighted by the flashing edit icon.',
                            'Admin'
                        )}
                    />
                )}

                {storeVariantsRemoved && (
                    <NotificationBanner
                        type='WARNING'
                        title={T._('Store variants not found', 'Admin')}
                        content={T._(
                            'It seems that all the variants for this product have been removed from your store. By keeping the variants configured in Zakeke, customers may still be asked to choose a variant that will appear as out-of-stock, causing a poor user experience. We recommend re-adding the variants in your store or removing the variant configuration in Zakeke.',
                            'Admin'
                        )}
                    />
                )}

                <Section
                    description={
                        colors.length > 0 ? (
                            <>
                                {T._(`Pick the images your buyers will see as they customize their orders.`, 'Admin')}
                                <br />
                                {T._(`Zakeke needs one image for each variation.`, 'Admin')}
                            </>
                        ) : null
                    }
                >
                    {colors.map((modelColor, index) => {
                        return (
                            <VariantRow
                                key={index}
                                color={modelColor}
                                showMoveDownButton={index != colors.length - 1}
                                showMoveUpButton={index != 0}
                                showEditButton={true}
                                onClickJsonSettings={() => onClickJsonSettings(modelColor)}
                                onMoveDownClick={() => onVariantMoveDownClick(modelColor)}
                                onMoveUpClick={() => onVariantMoveUpClick(modelColor)}
                                isSidesPerVariantEnabled={isSidesPerVariantEnabled}
                                onToggleClick={onSideToggleClick}
                                onAreaChangeClick={onSideAreaChangeClick}
                                onImageChangeClick={onSideImageChangeClick}
                                onRemoveClick={() => onVariantRemoveClick(modelColor)}
                                onEditClick={() => onVariantEditClick(modelColor)}
                                onSetMandatorySide={onSetMandatorySide}
                                onSetRotateSide={onSetRotateSide}
                                highlightEdit={mismatchedVariantGuids.has(modelColor.guid) && !fixedVariantGuids.has(modelColor.guid)}
                                showRemoveButton={!(state.product.colors.length == 1 && state.product.colors[0].colorID == 1)}
                            />
                        );
                    })}

                    {colors.length == 0 && (
                        <div
                            css={`
                                margin: 40px 0;
                            `}
                        >
                            <Center>
                                <PrimaryButton size='large' onClick={() => onVariantAddClick()}>
                                    {state.ecommerceSalesChannel.some((e) => e === 'NONE') && state.ecommerceSalesChannel.length == 1
                                        ? T._('Edit attributes', 'Admin')
                                        : T._('Add variation', 'Admin')}
                                </PrimaryButton>
                            </Center>
                        </div>
                    )}
                </Section>
            </VerticalLayout>

            {/* {state.product.additionalCodes.length == 0 && colors.length > 0 && <BasicButton onClick={() => onVariantAddClick()}>{(state.ecommerceSalesChannel.some(e=>e === 'NONE') && state.ecommerceSalesChannel.length == 1) ? T._('Go to variations','Admin') : T._('Add', 'Admin')}</BasicButton>} */}
            {colors.length > 0 && (
                <PageFooterButtons $fixed={true}>
                    <Button size='large' onClick={onPreviewClick}>
                        {T._(`Go to review`, 'Admin')}
                    </Button>
                </PageFooterButtons>
            )}
        </React.Fragment>
    );
};

export default VariantsList;
